import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import useAppSelector from 'hooks/useAppSelector';
import { ShopOpenStatus } from 'microshop-api';
import WithdrawalLink from 'components/Withdrawal/WithdrawalLink';

const StoreInfo = () => {
    const { t } = useTranslation();
    const info = useAppSelector(({ shop }) => shop?.info);

    if (!info) return null;

    const { shopOpenStatus, name, expiredMessage, notActivatedMessage, activationDate } = info;

    const isExpired = shopOpenStatus === ShopOpenStatus.Expired;
    const message = isExpired ? expiredMessage || t('shopExpired', 'This shop has expired.') : notActivatedMessage;

    return (
        <CenteredContainer>
            <StyledCard>
                <StatusIndicator status={shopOpenStatus} />
                {name && <ShopName>{name}</ShopName>}
                <CardText className="mb-3">{message}</CardText>

                {!isExpired && activationDate && (
                    <>
                        <CardText>{t('openingDatePrefix')}</CardText>
                        <DateDisplay>{activationDate.str}</DateDisplay>
                    </>
                )}
                <WithdrawalLinkRow>
                    <WithdrawalLink />
                </WithdrawalLinkRow>
            </StyledCard>
        </CenteredContainer>
    );
};

const StatusIndicator = styled.div<{ status?: ShopOpenStatus }>`
    height: 8px;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    background-color: ${({ status, theme }) => {
        if (status === ShopOpenStatus.Expired) return '#457b9d';
        if (status === ShopOpenStatus.NotActivated) return theme.colors.warning;
        return theme.colors.fillBlack;
    }};
`;

const CenteredContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    width: 100%;
    padding: 0 15px;
    background-color: rgb(244, 244, 244);
`;

const StyledCard = styled.div`
    width: 100%;
    max-width: 600px;
    background-color: white;
    padding: 2rem;
    overflow: hidden;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    position: relative;
    padding-top: 2.5rem;
`;

const ShopName = styled.h3`
    font-size: 1.5rem;
    font-weight: 600;
    line-height: 1.3;
    margin-top: 0.5rem;
    margin-bottom: 1.5rem;
    text-align: center;
    color: #000;
`;

const CardText = styled.p`
    font-size: 1rem;
    font-weight: 400;
    margin-top: 0;
    margin-bottom: 0;
    text-align: center;
    color: #000;
`;

const WithdrawalLinkRow = styled.div`
    margin-top: 1.5rem;
    text-align: center;

    &:empty {
        display: none;
    }
`;

const DateDisplay = styled.div`
    font-size: 1.35rem;
    font-weight: 700;
    line-height: 1.4;
    text-align: center;
    margin-top: 0;
    margin-bottom: 0.75rem;
    color: #000;
`;

export default StoreInfo;
